import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { JobTypeDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import type { RatingsStore } from '@/features/jobs/details/ratings/RatingsStore'
import { Id } from '@/infra/id'
import { action, computed, observable, runInAction } from 'mobx'
import { type JobRatingStatus } from '@/features/jobs/details/ratings/ratings-mappers'
import { tapAction, toStream } from '@/utils/mobx-util'
import { filter, switchMap } from 'rxjs/operators'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import type { SnackbarFactory } from '@/features/shell'
import { debounceTime, defer, mergeWith } from 'rxjs'
import { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { whileActive, withAbortSignal } from '@/utils/rx-util'
import { BookkeepingJobDetailsViewModel } from '@/features/jobs/details/bookkeeping-type/BookkeepingJobDetailsViewModel'
import type { TrustpilotCollectorViewModel } from '@/features/jobs/details/ratings/TrustpilotCollectorViewModel'
import { getPlatformRatingPresentation } from '@/features/jobs/details/ratings/rating-presentation'

export class RatingsViewModel implements ActivationLifecycle {
  /**
   * Manages the ViewModel's lifecycle, ensuring proper cleanup and activation.
   * @private
   */
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal ViewModel for Pro rating submission.
   */
  readonly proRatingModal = new ModalViewModel()

  /**
   * Modal ViewModel for Platform rating submission.
   */
  readonly platformRatingModal = new ModalViewModel()

  /**
   * Current rating status for Pro and Platform ratings.
   */
  @observable accessor ratingStatus: JobRatingStatus = {
    proRating: 'Unknown',
    platformRating: 'Unknown',
  }

  /**
   * Rating value provided by the user.
   */
  @observable accessor rating = 0

  /**
   * User-provided feedback text.
   */
  @observable accessor feedback = ''

  /**
   * Constructs the RatingsViewModel.
   * @param job The submitted job data.
   * @param typeVm ViewModel for the job type details.
   * @param ratingsStore Store managing ratings-related data.
   * @param _newSnackbar Factory to create snackbar notifications.
   */
  constructor(
    public readonly job: SubmittedJob,
    public readonly typeVm: JobTypeDetailsViewModel,
    public readonly ratingsStore: RatingsStore,
    private readonly _newSnackbar: SnackbarFactory,
    public readonly trustpilotCollectorVm: TrustpilotCollectorViewModel,
  ) {
    // Fetch rating status when certain aspects of the job deem it
    // necessary.
    const checkRatingStatus$ = defer(() => {
      const status$ = toStream(() => this.job.status, true)

      // Check whenever we're in draft-approved
      // if this is a tax job.
      if (
        typeVm instanceof TaxJobDetailsViewModel &&
        typeVm.progressionVm.progression
      ) {
        const progression = typeVm.progressionVm.progression
        return toStream(() => progression.current, true).pipe(
          filter((x) => x === 'DraftApproved'),
          mergeWith(status$),
          debounceTime(20),
        )
      }

      // Check whenever we're in Bookkeeping Completed for bookkeeping jobs.
      if (typeVm instanceof BookkeepingJobDetailsViewModel) {
        return toStream(
          () => typeVm.bookkeepingProgressionInfo?.stage,
          true,
        ).pipe(
          filter((stage) => stage === 'Completed'),
          mergeWith(status$),
          debounceTime(20),
        )
      }

      return status$
    })

    this._lifecycle.manage(
      checkRatingStatus$.pipe(
        switchMap(() =>
          withAbortSignal((signal) => this.fetchJobRatingStatus(signal)),
        ),
        filter(() => this.isRateable),
        tapAction(() => this.showRatingModal()),
        // Only subscribe to the pipeline if ratings are enabled.
        whileActive(toStream(() => this.isRatingsEnabled, true)),
      ),
    )
  }

  /**
   * Gets the Pro user associated with the job.
   */
  @computed
  get pro() {
    return this.job.championPro?.user
  }

  /**
   * Returns true if either Pro or Platform rating is in a Pending state, and
   * the current user is able to rate.
   */
  @computed
  get isRateable() {
    const { proRating, platformRating } = this.ratingStatus
    const isCurrentUserAbleToRate = this.job.championClient.user.isCurrentUser

    if (!this.isRatingsEnabled || !isCurrentUserAbleToRate) {
      return false
    }

    return proRating === 'Pending' || platformRating === 'Pending'
  }

  /**
   * Whether the job allows any form of ratings.
   */
  @computed
  get isRatingsEnabled() {
    const { proRatingEnabled, platformRatingEnabled } =
      this.job.legend.ratingConfig

    return proRatingEnabled || platformRatingEnabled
  }

  /**
   * Checks if the user rating is valid (between 1 and 5 inclusive).
   */
  @computed
  get isRatingValid() {
    return this.rating >= 1 && this.rating <= 5
  }

  @computed
  get shouldCollectTrustpilotReview() {
    return (
      this.job.workspace.config.review.trustpilotEnabled && this.isRatingValid
    )
  }

  @computed
  get platformRatingPresentation() {
    return getPlatformRatingPresentation(
      this.rating,
      this.job.workspace.config.review.trustpilotEnabled,
    )
  }

  @computed
  get shouldRequestPlatformFeedback() {
    return this.platformRatingPresentation.feedbackPrompt !== null
  }

  get isTaxJob() {
    return this.typeVm instanceof TaxJobDetailsViewModel
  }

  /**
   * Determines if ratings are enabled and valid for the current job.
   */
  @computed
  get ratingsEnabled() {
    const { proRatingEnabled, platformRatingEnabled } =
      this.job.legend.ratingConfig

    return (
      (proRatingEnabled || platformRatingEnabled) &&
      this.job.championClient.user.isCurrentUser
    )
  }

  /**
   * Activates the lifecycle manager for this ViewModel.
   */
  activate() {
    this._lifecycle.activate()
  }

  /**
   * Deactivates the lifecycle manager for this ViewModel.
   */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the current job rating status from the ratings store and updates the ViewModel state.
   */
  @action.bound
  async fetchJobRatingStatus(signal?: AbortSignal) {
    const ratingStatus = await this.ratingsStore.getRatingsStatus(
      Id.assert(this.job.id),
      signal,
    )

    signal?.throwIfAborted()

    runInAction(() => {
      this.ratingStatus = {
        ...ratingStatus,
        // Make sure we never show a Pro rating option if there's no
        // Pro on the job.
        proRating: this.pro ? ratingStatus.proRating : 'NotReady',
      }
    })
  }

  /**
   * Displays the appropriate rating modal based on the pending rating type.
   */
  @action.bound
  showRatingModal() {
    if (this.ratingStatus.proRating === 'Pending') {
      this.proRatingModal.show()
    } else if (this.ratingStatus.platformRating === 'Pending') {
      this.platformRatingModal.show()
    }
  }

  /**
   * Submits the Pro rating, displays a success snackbar, and handles states accordingly.
   */
  submitProRating = task.resolved(async () => {
    if (!this.pro) {
      return
    }

    const snackbar = this._newSnackbar((s) =>
      s
        .saying('Thank you!', 'Your Pro rating has been submitted.')
        .type('success')
        .autoDismiss(),
    )

    try {
      await this.ratingsStore.submitProRating({
        jobId: Id.assert(this.job.id),
        rating: this.rating,
        comment: this.feedback,
        proUserId: Id.assert(this.pro.id),
      })

      this.close()
      snackbar.show()
      await this.fetchJobRatingStatus()

      if (this.ratingStatus.platformRating === 'Pending') {
        this.platformRatingModal.show()
      }
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  })

  /**
   * Submits the Platform rating, displays a success snackbar, and updates the relevant states.
   */
  submitPlatformRating = task.resolved(async () => {
    const snackbar = this._newSnackbar((s) =>
      s
        .saying('Thank you!', 'Your platform rating has been submitted.')
        .type('success')
        .autoDismiss(),
    )

    try {
      const shouldCollectTrustpilotReview = this.shouldCollectTrustpilotReview
      const submittedRating = this.rating

      await this.ratingsStore.submitPlatformRating({
        jobId: Id.assert(this.job.id),
        rating: submittedRating,
        comment: this.shouldRequestPlatformFeedback ? this.feedback : '',
        proUserId: this.pro?.id,
      })

      this.platformRatingModal.close()
      this.reset()
      snackbar.show()
      if (shouldCollectTrustpilotReview) {
        this.trustpilotCollectorVm.show(submittedRating)
      }
      await this.fetchJobRatingStatus()
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  })

  /**
   * Sets the rating value provided by the user.
   * @param value The rating value to set.
   */
  @action.bound
  setRating(value: number) {
    this.rating = value
  }

  /**
   * Closes all rating modals and resets the rating and feedback state.
   */
  @action.bound
  close() {
    this.reset()
    this.proRatingModal.close()
    this.platformRatingModal.close()
    this.trustpilotCollectorVm.close()
  }

  /**
   * Resets the rating workflow to its initial state.
   */
  @action.bound
  reset() {
    this.rating = 0
    this.feedback = ''
    this.trustpilotCollectorVm.reset()
  }
}
